// Services the board reads with one key the user saves once, for every board
// in the workspace: Stripe's key, Lemon Squeezy, Plausible, beehiiv,
// Buttondown, YouTube and GitHub. The key is sealed on the board's server, is
// only ever sent to the service's own API hosts, and goes into a request only
// as the request goes out: in a header for most, in the address for YouTube.

import { fillTemplate, paramProblem, RECIPES, type ParamSpec, type RecipeSpec } from "./presets";
import type { HeaderSpec } from "./types";

export interface KeyService {
  id: string;
  name: string;
  /** what it shows, for its tile */
  blurb: string;
  /** the API hosts its key may go to, and nowhere else */
  hosts: string[];
  /** the paths on those hosts its key is for, when a host serves other APIs too (Google's) */
  paths?: string[];
  /** how the key travels: in a header, written by a template, or as a parameter in the address */
  auth: { header: string; template: string } | { query: string };
  /** headers every request to it sends, such as JSON:API's Accept */
  headers?: { name: string; value: string }[];
  /** what the service calls it */
  noun: "key" | "token";
  /** the field's label */
  keyLabel: string;
  placeholder?: string;
  /** what kind of key to make, when it matters */
  keyHelp?: string;
  /** where it is made */
  keyPage?: string;
  docs: string;
  /** the one read that checks a key when it is saved, and what that read needs besides the key */
  check: { url: string; params?: ParamSpec[] };
  /**
   * How the service says it turned the key itself away, as against a thing
   * the key cannot read: the statuses it answers with, and where it uses
   * those for other things too, the words that say it is the key (Google's).
   * maybe: it gives the very same answer for a thing the key cannot read
   * (Plausible, for a site), so only the key's own check can tell.
   */
  refusal: { status: number[]; says?: RegExp; maybe?: true };
  /**
   * What a key reads, when one key can read several (a Lemon Squeezy store):
   * the recipe parameter it fills, what to call it, where the check's answer
   * lists them, and where an older source's address names one.
   */
  account?: { param: string; noun: string; list?: { path: string; id: string; name: string }; fromUrl?: RegExp };
}

const bearer = { header: "Authorization", template: "Bearer {key}" };

export const KEY_SERVICES: KeyService[] = [
  {
    id: "stripe",
    name: "Stripe",
    blurb: "Your MRR and balance, with a restricted key",
    hosts: ["api.stripe.com"],
    auth: bearer,
    noun: "key",
    keyLabel: "Restricted key",
    placeholder: "rk_live_…",
    keyHelp: "Create a restricted key with Read on Balance and Subscriptions.",
    keyPage: "https://dashboard.stripe.com/apikeys",
    docs: "https://docs.stripe.com/api/balance/balance_retrieve",
    check: { url: "https://api.stripe.com/v1/balance" },
    // 403 is a restricted key without the right permission: the key itself is fine
    refusal: { status: [401] },
  },
  {
    id: "lemonsqueezy",
    name: "Lemon Squeezy",
    blurb: "Revenue and orders from your store",
    hosts: ["api.lemonsqueezy.com"],
    auth: bearer,
    headers: [{ name: "Accept", value: "application/vnd.api+json" }],
    noun: "key",
    keyLabel: "API key",
    placeholder: "eyJ0eXAi…",
    keyPage: "https://app.lemonsqueezy.com/settings/api",
    docs: "https://docs.lemonsqueezy.com/api",
    check: { url: "https://api.lemonsqueezy.com/v1/stores" },
    refusal: { status: [401] },
    account: { param: "store", noun: "store", list: { path: "data", id: "id", name: "attributes.name" }, fromUrl: /\/v1\/stores\/(\d+)|filter(?:\[|%5B)store_id(?:\]|%5D)=(\d+)/i },
  },
  {
    id: "plausible",
    name: "Plausible",
    blurb: "Visitors to your site",
    hosts: ["plausible.io"],
    auth: bearer,
    noun: "key",
    keyLabel: "API key",
    keyHelp: "A Stats API key.",
    keyPage: "https://plausible.io/settings/api-keys",
    docs: "https://plausible.io/docs/stats-api-v1",
    // every read names a site, so the check reads today's visitors on one
    check: {
      url: "https://plausible.io/api/v1/stats/aggregate?site_id={site}&period=day&metrics=visitors",
      params: [{ name: "site", label: "Your site", placeholder: "example.com", required: true, help: "As it is named in Plausible." }],
    },
    // "Invalid API key or site ID": the same 401 for a wrong key and for a site the key cannot read
    refusal: { status: [401], maybe: true },
    account: { param: "site", noun: "site", fromUrl: /[?&]site_id=([^&]+)/ },
  },
  {
    id: "beehiiv",
    name: "beehiiv",
    blurb: "Subscribers on your newsletter",
    hosts: ["api.beehiiv.com"],
    auth: bearer,
    noun: "key",
    keyLabel: "API key",
    docs: "https://developers.beehiiv.com/api-reference/publications/index",
    check: { url: "https://api.beehiiv.com/v2/publications" },
    refusal: { status: [401] },
    account: { param: "pub", noun: "publication", list: { path: "data", id: "id", name: "name" }, fromUrl: /\/v2\/publications\/(pub_[\w-]+)/ },
  },
  {
    id: "buttondown",
    name: "Buttondown",
    blurb: "Everyone on your newsletter",
    hosts: ["api.buttondown.com"],
    auth: { header: "Authorization", template: "Token {key}" },
    noun: "key",
    keyLabel: "API key",
    docs: "https://docs.buttondown.com/api-authentication",
    check: { url: "https://api.buttondown.com/v1/newsletters" },
    refusal: { status: [401] },
  },
  {
    id: "youtube",
    name: "YouTube",
    blurb: "Subscribers to a channel",
    hosts: ["www.googleapis.com"],
    // the same host serves Google's other APIs, which this key is not for
    paths: ["/youtube/"],
    auth: { query: "key" },
    noun: "key",
    keyLabel: "Google API key",
    keyHelp: "A key with the YouTube Data API turned on.",
    keyPage: "https://console.cloud.google.com/apis/credentials",
    docs: "https://developers.google.com/youtube/v3/docs/channels/list",
    check: { url: "https://www.googleapis.com/youtube/v3/i18nRegions?part=snippet" },
    // Google turns a key away with 400 or 403, and uses both for other things too (a quota, a private video): its words say which
    refusal: {
      status: [400, 403],
      says: /API key not valid|API key expired|API_KEY_(INVALID|EXPIRED|SERVICE_BLOCKED|HTTP_REFERRER_BLOCKED|IP_ADDRESS_BLOCKED|ANDROID_APP_BLOCKED|IOS_APP_BLOCKED)|SERVICE_DISABLED|accessNotConfigured|keyInvalid|keyExpired|has not been used in project|requests from (referer|this ip|this android|this ios)[^"]* are blocked|requests to this api [^"]* are blocked/i,
    },
  },
  {
    id: "github",
    name: "GitHub",
    blurb: "Private repositories, with your own token",
    hosts: ["api.github.com"],
    auth: bearer,
    headers: [{ name: "Accept", value: "application/vnd.github+json" }],
    noun: "token",
    keyLabel: "Token",
    placeholder: "github_pat_…",
    keyPage: "https://github.com/settings/personal-access-tokens/new",
    docs: "https://docs.github.com/en/rest/users/users#get-the-authenticated-user",
    check: { url: "https://api.github.com/user" },
    // 403 and 404 are a limit, or a repository the token cannot see
    refusal: { status: [401] },
  },
];

export const KEY_SERVICE: Record<string, KeyService> = Object.fromEntries(KEY_SERVICES.map((s) => [s.id, s]));

/** A key service by id, never one inherited from Object. */
export function keyService(id: unknown): KeyService | null {
  return typeof id === "string" && Object.hasOwn(KEY_SERVICE, id) ? KEY_SERVICE[id] : null;
}

/** Its recipes, in the order the board offers them. */
export function recipesOf(id: string): RecipeSpec[] {
  return RECIPES.filter((r) => r.service === id);
}

// ---------------------------------------------------------------------------
// How add_source reads what it is given: one rule, for the board and for its
// chat, which decides from it whether to ask the user and what to show them.

/** What add_source is given that decides how it reads. */
export interface SourceAsk {
  kind?: unknown;
  recipe?: unknown;
  service?: unknown;
  url?: unknown;
  params?: unknown;
}

export type SourceRead =
  /**
   * with the service's saved key, on its own host only. written: the service
   * is known by its key written in the address, which is never read with,
   * kept or saved: the saved key reads instead.
   */
  | { by: "key"; service: KeyService; recipe: RecipeSpec | null; written?: true }
  /** refused before anything is read */
  | { by: "refused"; error: string }
  /** the address it is given, as it is, with no saved key */
  | { by: "plain" };

/** What a saved key reads, as the board keeps it: never the key. */
export interface SavedAccount {
  account: { id: string; name: string | null } | null;
  accounts: { id: string; name: string | null }[];
}

const named = (v: unknown): string | null => (typeof v === "string" && v ? v : null);

/** The service whose key is written in an address on that service's own host and paths (YouTube's ?key=), if one is. */
export function keyWrittenFor(url: unknown): KeyService | null {
  if (typeof url !== "string") return null;
  const s = serviceForUrl(url.trim());
  return s && keyInUrl(s, url.trim()) ? s : null;
}

/**
 * How add_source reads: a recipe or a service reads with that service's
 * saved key, and so does an address that holds a known service's key, which
 * is never read with: whoever wrote it there, the user's saved key reads
 * instead, or nothing does. Only kind json reads with a saved key, so with
 * any other kind each of these is refused rather than read some other way.
 * A recipe or a service named at all, even empty, counts. Anything else
 * reads the address it is given.
 */
export function sourceRead(s: SourceAsk): SourceRead {
  const recipeId = named(s.recipe);
  const serviceId = named(s.service);
  // a preset reads no address, so one that came with it is never read
  const written = !recipeId && !serviceId && s.kind !== "preset" ? keyWrittenFor(s.url) : null;
  const kind = typeof s.kind === "string" && s.kind ? `, not ${s.kind}` : "";
  if (written && s.kind !== "json")
    return { by: "refused", error: `That address holds a ${written.name} ${written.noun}. The board reads ${written.name} only with the user's saved ${written.noun}, which add_source does only for kind json${kind}. Nothing was read.` };
  if (written) return { by: "key", service: written, recipe: null, written: true };
  if ((typeof s.recipe === "string" || typeof s.service === "string") && s.kind !== "json")
    return { by: "refused", error: `A recipe or a service reads with the user's saved key, which add_source does only for kind json${kind}. Nothing was read. Pass kind json, or leave recipe and service out to read a feed or a calendar.` };
  if (!recipeId && !serviceId) return { by: "plain" };
  const recipe = recipeId ? (RECIPES.find((r) => r.id === recipeId) ?? null) : null;
  if (recipeId && !recipe) return { by: "refused", error: `No recipe called ${recipeId}. list_connections lists each connected service's recipes.` };
  const service = keyService(recipe?.service ?? serviceId);
  if (!service) return { by: "refused", error: `No service called ${String(serviceId)}. The services are ${KEY_SERVICES.map((x) => x.id).join(", ")}.` };
  if (recipe && serviceId && serviceId !== recipe.service) return { by: "refused", error: `The ${recipe.id} recipe reads ${service.name}, not ${serviceId}.` };
  return { by: "key", service, recipe };
}

/**
 * Reads joined from several of one service's lists (a recipe's compose), as
 * worker/pulls.ts makes them: every address each reads besides its recipe's
 * own, each a GET on the service's own host read page by page, and what a
 * parameter must be for the read to run at all. Payments reads the store's
 * orders, its subscription invoices, and the subscriptions its renewals name.
 */
const JOINED: Record<string, { also: string[]; params?: Record<string, { test: RegExp; is: string }> }> = {
  "lemonsqueezy-payments": {
    also: ["subscription-invoices", "subscriptions"].map((list) => `https://api.lemonsqueezy.com/v1/${list}?filter[store_id]={store}&page[size]=100`),
    params: { store: { test: /^\d{1,20}$/, is: "the store's number, like 12345" } },
  },
  // MRR reads the store's active and past due subscriptions, and the price of each plan they are on
  "lemonsqueezy-mrr": {
    also: [...["active", "past_due"].map((status) => `https://api.lemonsqueezy.com/v1/subscriptions?filter[store_id]={store}&filter[status]=${status}&page[size]=100`), "https://api.lemonsqueezy.com/v1/prices/(each plan's price)"],
    params: { store: { test: /^\d{1,20}$/, is: "the store's number, like 12345" } },
  },
  "stripe-mrr": { also: ["https://api.stripe.com/v1/subscriptions?status=past_due&limit=100"] },
  // Stripe's revenue and payments read the balance's transactions back to the start of the period before
  "stripe-revenue": { also: [] },
  "stripe-payment-count": { also: [] },
  // Orders reads the store's orders back to the start of the period before; Revenue its subscription invoices too
  "lemonsqueezy-order-count": { also: [], params: { store: { test: /^\d{1,20}$/, is: "the store's number, like 12345" } } },
  "lemonsqueezy-revenue": {
    also: ["https://api.lemonsqueezy.com/v1/subscription-invoices?filter[store_id]={store}&page[size]=100"],
    params: { store: { test: /^\d{1,20}$/, is: "the store's number, like 12345" } },
  },
};

/**
 * The address a read with a saved key starts from, filled in the way the
 * board fills it: a recipe's parameters from what it is given, then from the
 * account the key reads, then from their defaults. Never with the key in it.
 * The board reads with it and the chat asks the user about it, so each
 * refuses the same reads: a parameter that would walk out of its recipe's
 * endpoint, an address off the service's own host, and an address with a .
 * or .. part, which would not read what it says. urls: every address the
 * read uses, this one first (a joined read uses several). params: what it
 * filled in.
 */
export function keyedAddress(read: Extract<SourceRead, { by: "key" }>, s: SourceAsk, saved: SavedAccount): { ok: true; url: string; urls: string[]; params: Record<string, string> } | { ok: false; error: string } {
  const { service: svc, recipe } = read;
  if (recipe) {
    const given = s.params && typeof s.params === "object" ? (s.params as Record<string, unknown>) : undefined;
    const params: Record<string, string> = {};
    for (const ps of recipe.params) {
      const raw = given?.[ps.name] ?? (ps.account ? saved.account?.id : undefined) ?? ps.default;
      const v = raw === undefined || raw === null ? "" : String(raw).trim();
      if (v) params[ps.name] = v.slice(0, 200);
      else if (ps.required)
        return {
          ok: false,
          error:
            ps.account && saved.accounts.length > 1
              ? `This ${svc.name} ${svc.noun} reads more than one ${svc.account?.noun ?? "account"}. Pass params.${ps.name} as one of: ${saved.accounts.map((a) => `${a.id}${a.name ? ` (${a.name})` : ""}`).join(", ")}.`
              : `The ${recipe.label.toLowerCase()} recipe needs params.${ps.name} (${ps.label.toLowerCase()}${ps.placeholder ? `, like ${ps.placeholder}` : ""}).`,
        };
    }
    // every parameter stays inside the recipe's endpoint: none walks out of it with a slash or a . or .. part
    const bad = paramProblem(recipe.url, params);
    if (bad) return { ok: false, error: bad };
    const joined = recipe.compose ? JOINED[recipe.compose] : undefined;
    for (const [name, p] of Object.entries(joined?.params ?? {})) if (!p.test.test(params[name] ?? "")) return { ok: false, error: `The ${recipe.label.toLowerCase()} recipe needs params.${name} as ${p.is}.` };
    const url = fillTemplate(recipe.url, params);
    return { ok: true, url, urls: [url, ...(joined?.also ?? []).map((t) => fillTemplate(t, params))], params };
  }
  const url = typeof s.url === "string" ? s.url.trim() : "";
  if (!onServiceHost(svc, url)) return { ok: false, error: `The board sends your ${svc.name} ${svc.noun} only to ${hostsOf(svc)}. Give an https address there, like ${svc.check.url.split("?")[0]}.` };
  if (hasDotSegments(url)) return { ok: false, error: `That address has a . or .. part in its path, so it would not read what it says. Write the ${svc.name} address in full.` };
  // a key written in the address is never read with: the saved key goes in as the request goes out
  const bare = withoutKey(svc, url);
  return { ok: true, url: bare, urls: [bare], params: {} };
}

/**
 * What add_source must be given for a recipe besides what the saved key fills
 * in: each required parameter with no default, the account one included when
 * the board does not know which account the key reads. keyedAddress refuses a
 * recipe without them, so whatever tells an agent what a recipe needs says this.
 */
export function paramsNeeded(recipe: RecipeSpec, saved: SavedAccount | null | undefined): ParamSpec[] {
  return recipe.params.filter((p) => p.required && p.default === undefined && !(p.account && saved?.account));
}

/** A parameter as an agent passes it: "params.repo (repository, like owner/name)". */
export function paramHint(p: ParamSpec): string {
  return `params.${p.name} (${p.label.toLowerCase()}${p.placeholder ? `, like ${p.placeholder}` : ""})`;
}

/** Whether an address is on one of the service's own API hosts (and paths), over https: the only places its key is sent. */
export function onServiceHost(s: KeyService, url: string | null | undefined): boolean {
  if (!url) return false;
  try {
    const u = new URL(url);
    if (u.protocol !== "https:" || u.username || u.password || (u.port && u.port !== "443")) return false;
    if (!s.hosts.includes(u.hostname.toLowerCase().replace(/\.$/, ""))) return false;
    return !s.paths || s.paths.some((p) => u.pathname.startsWith(p));
  } catch {
    return false;
  }
}

/** The service an address belongs to, by its host. */
export function serviceForUrl(url: string | null | undefined): KeyService | null {
  return KEY_SERVICES.find((s) => onServiceHost(s, url)) ?? null;
}

/** "Bearer " for a key sent as "Bearer {key}". */
function prefixOf(s: KeyService): string {
  return "header" in s.auth ? s.auth.template.slice(0, s.auth.template.indexOf("{key}")) : "";
}

/** The bare key from what someone pasted, or from a header written with the service's template: "Bearer abc" is abc. */
export function bareKey(s: KeyService, value: string): string {
  const v = value.trim();
  const pre = prefixOf(s);
  return pre && v.toLowerCase().startsWith(pre.toLowerCase()) ? v.slice(pre.length).trim() : v;
}

/** The key in a header written with the service's own template ("Bearer abc" is abc), or null when the header is not in that form. */
export function keyFromHeader(s: KeyService, value: string): string | null {
  if (!("header" in s.auth)) return null;
  const pre = prefixOf(s);
  if (pre && !value.trim().toLowerCase().startsWith(pre.toLowerCase())) return null;
  const key = bareKey(s, value);
  return key && !/\s/.test(key) ? key : null;
}

/** The header a key goes in, for a service that takes it in one. */
export function keyHeader(s: KeyService, key: string): HeaderSpec | null {
  return "header" in s.auth ? { name: s.auth.header, value: s.auth.template.replace("{key}", key), secret: true } : null;
}

/**
 * Whether a header carries a credential of its own: Authorization, a cookie,
 * or anything named for a key, token or secret. A request that goes out with
 * a saved key carries no credential but the key's own.
 */
export function credentialHeader(name: string): boolean {
  const n = name.trim().toLowerCase();
  return /^(authorization|proxy-authorization|cookie)$/.test(n) || /key|token|secret|auth|session|cookie|password|credential/.test(n);
}

/**
 * Whether the service's answer says it turned the key itself away: yes; maybe
 * when it gives the same answer for a thing the key cannot read; or null.
 */
export function keyRefusal(s: KeyService, status: number, text: string): "yes" | "maybe" | null {
  const r = s.refusal;
  if (!r.status.includes(status)) return null;
  if (r.says && !r.says.test(text.slice(0, 8000))) return null;
  return r.maybe ? "maybe" : "yes";
}

/**
 * Whether an address's path steps out of where it is written, with a . or ..
 * part, plain or escaped as %2e. No API address needs one, and an address
 * that has one does not read what it says.
 */
export function hasDotSegments(url: string): boolean {
  const m = /^[a-z][a-z0-9+.-]*:[\\/]{2}[^\\/?#]*([^?#]*)/i.exec(url.trim());
  return !!m && m[1].split(/[\\/]/).some((part) => /^(\.|%2e){1,2}$/i.test(part));
}

/** The address with the key in it, for a service that takes it there: made as the request goes out, never kept. The key goes before any #fragment, which a request never sends. */
export function withKey(s: KeyService, url: string, key: string): string {
  if (!("query" in s.auth)) return url;
  const bare = withoutKey(s, url);
  const hash = bare.indexOf("#");
  const head = hash < 0 ? bare : bare.slice(0, hash);
  const join = !head.includes("?") ? "?" : /[?&]$/.test(head) ? "" : "&";
  return `${head}${join}${s.auth.query}=${encodeURIComponent(key)}${hash < 0 ? "" : bare.slice(hash)}`;
}

/** The address without the key's parameter, so it can be kept. */
export function withoutKey(s: KeyService, url: string): string {
  if (!("query" in s.auth)) return url;
  const name = s.auth.query;
  const [head, ...rest] = url.split("#");
  const q = head.indexOf("?");
  if (q < 0) return url;
  const nameOf = (part: string) => {
    const k = part.split("=")[0];
    try {
      return decodeURIComponent(k);
    } catch {
      return k;
    }
  };
  const kept = head
    .slice(q + 1)
    .split("&")
    .filter((part) => part && nameOf(part) !== name);
  return head.slice(0, q) + (kept.length ? "?" + kept.join("&") : "") + (rest.length ? "#" + rest.join("#") : "");
}

/** The key written in an address, for a service that takes it there. */
export function keyInUrl(s: KeyService, url: string): string | null {
  if (!("query" in s.auth)) return null;
  try {
    const v = new URL(url).searchParams.get(s.auth.query);
    return v && v.trim() ? v.trim() : null;
  } catch {
    return null;
  }
}

/** The account an older source's address names, such as the store in /v1/stores/335290. */
export function accountInUrl(s: KeyService, url: string): string | null {
  const m = s.account?.fromUrl?.exec(url);
  const id = m ? m.slice(1).find((x) => x !== undefined) : undefined;
  if (!id) return null;
  try {
    return decodeURIComponent(id);
  } catch {
    return id;
  }
}

/** The check's address, with what it needs filled in. */
export function checkUrl(s: KeyService, params: Record<string, string>): string {
  return fillTemplate(s.check.url, params);
}

/** Its hosts, said in words: "api.lemonsqueezy.com". */
export function hostsOf(s: KeyService): string {
  return s.hosts.join(" or ");
}
